import { useCallback, useSyncExternalStore } from 'react';

/**
 * Subscribe to a media query.
 *
 * Only for layout that has to *branch*, not restyle. Rendering both variants
 * and hiding one with `md:hidden` leaves both copies of a control in the DOM,
 * so every button answers to two elements — fine for a link, wrong for a
 * toolbar whose buttons are addressed by name.
 *
 * jsdom ships no `matchMedia`, so an absent implementation reports `false`
 * rather than throwing.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback((onChange: () => void) => {
    const mql = window.matchMedia?.(query);
    if (!mql) return () => {};
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  const getSnapshot = useCallback(
    () => window.matchMedia?.(query).matches ?? false,
    [query],
  );

  // Server snapshot: the wide layout, which is the one that degrades safely.
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** The `md` breakpoint, matching Tailwind's. Below it the tab bar is shown. */
export const MOBILE_QUERY = '(max-width: 767px)';
